import { useEffect, useState } from 'react'

import { GraphQLClient } from 'graphql-request'
import { arbitrum, base, mainnet } from 'viem/chains'
// eslint-disable-next-line no-restricted-imports
import { useAccount, useSwitchChain, useAccount as useWagmiAccount } from 'wagmi'

import {
  FuulGraphUrl,
  rpcUrls,
  SupportedChainIdType,
  V1GraphUrls,
  isSupportedChain,
  IndexesGraphUrls,
} from '../constants/network'
import { useCryptexProductsContext } from '../contexts'


export const useAddress = () => {
  const { address: wagmiAddress } = useWagmiAccount()

  const [addressInfo, setAddressInfo] = useState<{ address: `0x${string}` | undefined; overriding: boolean }>({
    address: undefined,
    overriding: false,
  })

  useEffect(() => {
    setAddressInfo({ address: wagmiAddress, overriding: false })
  }, [wagmiAddress])

  return addressInfo
}

export const useChainId = () => { 
  let { chain } = useAccount()
  const products = useCryptexProductsContext()
  
  chain = chain ?? products.getDefaultChain()

  if (chain === undefined || !products.isProductSupportedChain(chain.id)) {
    return products.getDefaultChain().id as SupportedChainIdType;
  }

  return chain.id as SupportedChainIdType;
}

export const useCurrentChain = () => { 
  let { chain } = useAccount()
  const products = useCryptexProductsContext()
  
  chain = chain ?? products.getDefaultChain()

  if (chain === undefined || !products.isProductSupportedChain(chain.id)) {
    return products.getDefaultChain();
  }

  return chain;
}


export const useOnSupportedChain = () => { 
  let { chain } = useAccount();
  if (chain) {
    return { isUsingSupportedChain: isSupportedChain(chain.id) }
  }
  return { isUsingSupportedChain: true }
}


export const useRPCProviderUrl = (): string => {
  const products = useCryptexProductsContext();
  const chainId = products.getDefaultChain().id as SupportedChainIdType;

  return rpcUrls[chainId]
}


const indexesGraphClients = new Map<SupportedChainIdType, GraphQLClient>()
export const useIndexesGraphClient = () => {
  const chainId = useChainId()

  if (!indexesGraphClients.has(chainId)) indexesGraphClients.set(chainId, new GraphQLClient(IndexesGraphUrls[chainId]))

  return indexesGraphClients.get(chainId)!
}

const v1GraphClients = new Map<SupportedChainIdType, GraphQLClient>()
export const useV1GraphClient = () => { 
  const chainId = useChainId()

  if (!v1GraphClients.has(chainId)) v1GraphClients.set(chainId, new GraphQLClient(V1GraphUrls[chainId]))

  return v1GraphClients.get(chainId)!
}

export const useFuulGraphClient = () => { 
  return new GraphQLClient(FuulGraphUrl)
}

export const useUniswapV3GraphClient = () => {
  const apiKey = process.env.REACT_APP_THE_GRAPH_API_KEY;
  const url = `https://gateway.thegraph.com/api/${apiKey}/subgraphs/id/43Hwfi3dJSoGpyas9VwNoDAv55yjgGrPpNSmbQZArzMG`;
  return new GraphQLClient(url)
}

export const useUniswapPricesGraphClient = () => {
  const url = `https://interface.gateway.uniswap.org/v1/graphql`;
  return new GraphQLClient(url)
}


export const useChainActions = () => {
  let { chain } = useAccount()
  const { switchChainAsync, switchChain } = useSwitchChain();
  const { isIndexes, isGovernance, isSpot, isProductSupportedChain } = useCryptexProductsContext();

  const onSwitchChain = async () => { 
    if (chain && !isSpot() && !isProductSupportedChain(chain?.id)) {
      const toChainId =
        isIndexes() ? base.id : (isGovernance() ? mainnet.id : arbitrum.id);

      try {
        await switchChainAsync({ chainId: toChainId })
        return true;
      } catch (error) {
        return false;
      }
    }
    return true;
  }

  const onSwitchChain2 = () => { 
    if (chain && !isSpot() && !isProductSupportedChain(chain?.id)) {
      const toChainId =
        isIndexes() ? base.id : (isGovernance() ? mainnet.id : arbitrum.id);

      try {
        switchChain({ chainId: toChainId })
        return true;
      } catch (error) {
        return false;
      }
    }
    return true;
  }

  return {
    onSwitchChain,
    onSwitchChain2
  }
}
